import { useOrganizationStore } from '@/stores/organizationStore';
import { useOrganizationContext } from '@/providers/OrganizationProvider';

/**
 * Organization hook - provides organization state and methods.
 * Reads from OrganizationContext (backed by TanStack Query). Adds mutations and derived helpers.
 *
 * For read-only access, use useOrganizationContext() instead.
 */
export const useOrganization = () => {
  const organizationStore = useOrganizationStore();
  const context = useOrganizationContext();
  const setCurrentOrganization = (organization: { slug: string } | null) => {
    if (!organization?.slug) return;
    void organizationStore.switchOrganization(organization.slug);
  };

  return {
    // From context (TanStack Query)
    currentOrganization: context.currentOrganization,
    currentMembership: context.currentMembership,
    organizations: context.currentOrganization ? [context.currentOrganization] : [],
    memberships: context.currentMembership ? [context.currentMembership] : [],
    isLoading: context.isLoading,
    isError: context.isError,
    isFetched: context.isFetched,
    error: context.error,
    updateOrganization: context.updateOrganization,
    isUpdatingOrganization: context.isUpdatingOrganization,
    invalidateOrganization: context.invalidateOrganization,

    // From store (app state and helpers)
    setCurrentOrganization,
    hasRole: organizationStore.hasRole,
    switchOrganization: organizationStore.switchOrganization,

    // Currency methods (resolved via query cache)
    getCurrentCurrency: organizationStore.getCurrentCurrency,
    formatAmount: organizationStore.formatAmount,
    getCurrencySymbol: organizationStore.getCurrencySymbol,
  };
};
